/**
 * Businesses Page - List all businesses on the platform
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ExternalLink, MoreVertical } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.mango-business.solutions/api/v1';

interface Business {
  id: string;
  mangoId: string;
  name: string;
  email: string;
  website: string;
  businessType: string;
  planType: string;
  subscriptionStatus: string;
  monthlyFee: number;
  createdAt: string;
}

const Businesses: React.FC = () => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBusinesses();
  }, []);

  const loadBusinesses = async () => {
    try {
      const token = localStorage.getItem('platformAdminToken');
      const response = await fetch(`${API_BASE_URL}/platform/businesses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBusinesses(data.businesses || []);
      }
    } catch (error) {
      console.error('Failed to load businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBusinesses = businesses.filter(b => 
    b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.mangoId.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <div style={{ color: '#9ca3af' }}>Loading businesses...</div>;
  }

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Businesses</h1>
          <p style={styles.subtitle}>{businesses.length} businesses on the platform</p>
        </div>
      </div>

      {/* Search */}
      <div style={styles.searchContainer}>
        <Search size={20} style={{ color: '#9ca3af' }} />
        <input
          type="text"
          placeholder="Search by name, email, or Mango ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Table */}
      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Business</th>
              <th style={styles.th}>Mango ID</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Plan</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Monthly Fee</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBusinesses.map((business) => (
              <tr key={business.id}>
                <td style={styles.td}>
                  <div>
                    <div style={{ fontWeight: 500 }}>{business.name}</div>
                    <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>{business.email}</div>
                  </div>
                </td>
                <td style={styles.td}>
                  <code style={styles.code}>{business.mangoId}</code>
                </td>
                <td style={styles.td}>{business.businessType || '-'}</td>
                <td style={styles.td}>
                  <span style={{
                    ...styles.badge,
                    background: business.planType === 'enterprise' ? 'rgba(139, 92, 246, 0.2)' : 
                               business.planType === 'growth' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(156, 163, 175, 0.2)',
                    color: business.planType === 'enterprise' ? '#8B5CF6' :
                           business.planType === 'growth' ? '#22C55E' : '#9ca3af',
                  }}>
                    {business.planType}
                  </span>
                </td>
                <td style={styles.td}>
                  <span style={{
                    ...styles.badge,
                    background: business.subscriptionStatus === 'active' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                    color: business.subscriptionStatus === 'active' ? '#22C55E' : '#EF4444',
                  }}>
                    {business.subscriptionStatus}
                  </span>
                </td>
                <td style={styles.td}>${business.monthlyFee?.toFixed(2) || '0.00'}</td>
                <td style={styles.td}>
                  <Link to={`/businesses/${business.id}`} style={styles.link}>
                    <ExternalLink size={16} /> View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: {
    marginBottom: '30px',
  },
  title: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#fff',
    margin: 0,
  },
  subtitle: {
    color: '#9ca3af',
    marginTop: '5px',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: '#1a1a2e',
    borderRadius: '8px',
    padding: '12px 16px',
    border: '1px solid #252540',
    marginBottom: '20px',
  },
  searchInput: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    color: '#fff',
    fontSize: '1rem',
    outline: 'none',
  },
  tableCard: {
    background: '#1a1a2e',
    borderRadius: '12px',
    border: '1px solid #252540',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '16px 20px',
    color: '#9ca3af',
    fontWeight: 500,
    fontSize: '0.85rem',
    borderBottom: '1px solid #252540',
  },
  td: {
    padding: '16px 20px',
    borderBottom: '1px solid #252540',
  },
  code: {
    background: '#0f0f1a',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '0.85rem',
    color: '#FFA500',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 500,
    textTransform: 'capitalize',
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#FFA500',
    textDecoration: 'none',
    fontSize: '0.9rem',
  },
};

export default Businesses;
